import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Building2,
  ClipboardList,
  DollarSign,
  Dumbbell,
  Settings,
  Trophy,
  Users,
  UtensilsCrossed,
  Video,
} from 'lucide-react';
import { useI18n } from '../../context/useI18n';
import SpiritualHomeButton from '../spiritual/user/SpiritualHomeButton';
import { canShowSpiritualForUser } from '../../utils/spiritualApi';
import { useAuth } from '../../context/useAuth';
import { setOrgInitialTab } from '../../utils/organizationView';
import api from '../../services/api';
import { canAccessFitLive } from '../../utils/fitLiveModule';
import '../../styles/ecosystem-fit-ux.css';
import '../../styles/finance-board.css';
import '../../fit-live/styles/fit-live-shell.css';

function formatMoney(n, currency = 'COP') {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-CO', {
      style: 'currency',
      currency: currency === 'USD' ? 'USD' : 'COP',
      maximumFractionDigits: currency === 'USD' ? 2 : 0,
    }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString()}`;
  }
}

const DOMAIN_SECTIONS = [
  {
    id: 'platform',
    titleKey: 'superadmin.section.platform',
    titleFallback: 'Plataforma',
    subtitleKey: 'superadmin.section.platform_desc',
    subtitleFallback: 'Usuarios globales, organizaciones, maestros y configuración del ecosistema.',
    links: [
      { id: 'adminusers', labelKey: 'nav.users', fallback: 'Usuarios', domainTab: 'ecosystem-finals', Icon: Users },
      { id: 'asignaciones', labelKey: 'assignments.tab', fallback: 'Asignaciones', Icon: Users },
      { id: 'organizations', labelKey: 'nav.organizations', fallback: 'Organizaciones', Icon: Building2 },
      { id: 'organizations', orgTab: 'gimnasios', labelKey: 'orgs.tab.gyms', fallback: 'Gimnasios', Icon: Building2 },
      { id: 'masters', labelKey: 'nav.masters', fallback: 'Maestros', Icon: Settings },
    ],
  },
  {
    id: 'food',
    titleKey: 'superadmin.section.food',
    titleFallback: 'FOOD PLAN',
    subtitleKey: 'superadmin.section.food_desc',
    subtitleFallback: 'Nutrición, planes y usuarios del módulo alimentación.',
    links: [
      { id: 'panel:food-plan', labelKey: 'nav.food_plan', fallback: 'Panel FOOD', panel: 'food-plan', Icon: UtensilsCrossed },
      { id: 'adminusers', labelKey: 'nav.users', fallback: 'Usuarios FOOD', domainTab: 'food-clients', Icon: Users },
      { id: 'finance-food', labelKey: 'finance.food', fallback: 'Finanzas FOOD', Icon: DollarSign },
    ],
  },
  {
    id: 'training',
    titleKey: 'superadmin.section.training',
    titleFallback: 'Training — Coaches',
    subtitleKey: 'superadmin.section.training_desc',
    subtitleFallback: 'Coaches, rutinas, biblioteca de ejercicios y seguimiento.',
    links: [
      { id: 'panel:training', labelKey: 'nav.trainers_admin', fallback: 'Maestros Training', panel: 'training', Icon: Dumbbell },
      { id: 'adminusers', labelKey: 'nav.users', fallback: 'Clientes coaches', domainTab: 'training-clients', Icon: Users },
      { id: 'training-stats', labelKey: 'nav.training_stats', fallback: 'Estadísticas Training', Icon: BarChart3 },
      { id: 'finance-training', labelKey: 'finance.training', fallback: 'Finanzas Training', Icon: DollarSign },
      { id: 'organizations', orgTab: 'coaches', labelKey: 'orgs.tab.coaches', fallback: 'Organizaciones coaches', Icon: Dumbbell },
    ],
  },
  {
    id: 'academia',
    titleKey: 'superadmin.section.academia',
    titleFallback: 'academia.app',
    subtitleKey: 'superadmin.section.academia_desc',
    subtitleFallback: 'Clubs, escuelas, dar acceso y SSO al Nest (independiente de Food/Training).',
    links: [
      { id: 'panel:futbol', labelKey: 'nav.academia_masters', fallback: 'Maestros academia.app', panel: 'futbol', Icon: Trophy },
      { id: 'masters', labelKey: 'nav.masters', fallback: 'Hub Maestros', Icon: Settings },
    ],
  },
];

/** Home super admin — dominios separados (no mezclar tarjetas de consumo). */
export default function SuperAdminHomeCenter({
  onNavigate,
  onOpenServicePanel,
  onOpenSpiritual,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const showSpiritual = canShowSpiritualForUser(user);
  const fitLive = canAccessFitLive(user);
  const [financeByModule, setFinanceByModule] = useState({ food: null, training: null });
  const [financeErr, setFinanceErr] = useState('');

  const sections = useMemo(() => DOMAIN_SECTIONS, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const modules = ['food', 'training'];
        const results = await Promise.all(
          modules.map((scope_module) => api.get('/platform/finance/summary', {
            params: { period: 'month', currency: 'COP', scope_module },
          }).then((res) => [scope_module, res.data?.kpis || null]).catch(() => [scope_module, null])),
        );
        if (!cancelled) {
          const next = { food: null, training: null };
          for (const [mod, kpis] of results) next[mod] = kpis;
          setFinanceByModule(next);
          setFinanceErr('');
        }
      } catch (err) {
        if (!cancelled) {
          setFinanceByModule({ food: null, training: null });
          setFinanceErr(err?.response?.data?.error || err.message || 'Error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleLink = (link) => {
    if (link.domainTab) {
      try {
        sessionStorage.setItem('admin.users.domainTab', link.domainTab);
      } catch { /* ignore */ }
      onNavigate?.('adminusers');
      return;
    }
    if (link.orgTab) {
      setOrgInitialTab(link.orgTab);
    }
    if (link.panel) {
      onOpenServicePanel?.(link.panel, link.view || 'servicePanel');
      return;
    }
    if (link.id === 'finance-food') {
      onNavigate?.('finance-food');
      return;
    }
    if (link.id === 'finance-training') {
      onNavigate?.('finance-training');
      return;
    }
    onNavigate?.(link.id);
  };

  const openFinance = (mod) => {
    if (mod === 'food') onNavigate?.('finance-food');
    else if (mod === 'training') onNavigate?.('finance-training');
    else onNavigate?.('finance');
  };

  return (
    <div className="dashboard-main-view surface-readable space-y-8">
      <header>
        <h1 className="d28d-page-title text-2xl">{t('nav.home', 'Inicio')}</h1>
        <p className="d28d-text-muted text-sm mt-1">
          {fitLive
            ? 'Operación por dominio: plataforma, Fit Live, FOOD, Entrenadores y academia.app — sin mezclar dashboards.'
            : t('superadmin.home.subtitle', 'Operación por dominio: plataforma, FOOD, Entrenadores y academia.app — sin mezclar dashboards.')}
        </p>
      </header>

      {fitLive ? (
        <section className="space-y-3">
          <div>
            <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
              Fit Live
            </h2>
            <p className="text-sm d28d-text-muted mt-0.5">
              Marketplace · Creator Studio · Control Tower
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <button
              type="button"
              className="fl-home-card card p-4 text-left transition flex gap-3 items-start"
              onClick={() => onOpenServicePanel?.('fit-live', 'servicePanel')}
            >
              <Video className="w-5 h-5 fl-home-card__icon shrink-0 mt-0.5" aria-hidden />
              <span>
                <span className="fl-home-card__title block">Abrir Fit Live</span>
                <span className="fl-home-card__meta text-sm">Creator Studio y marketplace</span>
              </span>
            </button>
          </div>
        </section>
      ) : null}

      <section className="card p-4 ui-light-panel surface-readable space-y-3">
        <div>
          <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-lime-600" aria-hidden />
            {t('finance.title', 'Resumen financiero')}
          </h2>
          <p className="text-sm d28d-text-muted mt-0.5">
            {t('finance.home_preview_modules', 'Mes actual (COP), separado por producto. Cada tablero usa el mismo alcance que el módulo.')}
          </p>
        </div>
        {financeErr ? (
          <p className="text-sm text-red-700">{financeErr}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              ...(fitLive ? [{ id: 'fit-live', label: 'Fit Live', placeholder: true }] : []),
              { id: 'food', label: 'FOOD PLAN' },
              { id: 'training', label: 'Training' },
            ].map((mod) => {
              if (mod.placeholder) {
                return (
                  <button
                    key={mod.id}
                    type="button"
                    className="finance-kpi-card d28d-admin-surface ui-light-panel surface-readable text-left"
                    onClick={() => onOpenServicePanel?.('fit-live', 'servicePanel')}
                  >
                    <div>
                      <p className="finance-kpi-card__label">{mod.label}</p>
                      <p className="finance-kpi-card__hint">Tokens · Fase 4</p>
                    </div>
                    <p className="finance-kpi-card__value">Control Tower</p>
                  </button>
                );
              }
              const kpis = financeByModule[mod.id];
              return (
                <button
                  key={mod.id}
                  type="button"
                  className="finance-kpi-card d28d-admin-surface ui-light-panel surface-readable text-left"
                  onClick={() => openFinance(mod.id)}
                >
                  <div>
                    <p className="finance-kpi-card__label">{mod.label}</p>
                    <p className="finance-kpi-card__hint">
                      {t('finance.kpi.revenue', 'Ingresos')} · {kpis ? formatMoney(kpis.revenue) : '…'}
                    </p>
                  </div>
                  <p className="finance-kpi-card__value">
                    {kpis ? String(kpis.active) : '…'} {t('finance.kpi.active', 'activos')}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {sections.map((section) => (
        <section key={section.id} className="space-y-3">
          <div>
            <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide">
              {t(section.titleKey, section.titleFallback)}
            </h2>
            <p className="text-sm d28d-text-muted mt-0.5">
              {t(section.subtitleKey, section.subtitleFallback)}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {section.links.map((link) => (
              <button
                key={`${section.id}-${link.id}-${link.domainTab || link.orgTab || link.view || ''}`}
                type="button"
                className="card p-4 text-left hover:ring-2 hover:ring-lime-400/40 transition flex gap-3 items-start"
                onClick={() => handleLink(link)}
              >
                <link.Icon className="w-5 h-5 text-lime-600 shrink-0 mt-0.5" aria-hidden />
                <span>
                  <span className="font-semibold text-[var(--d28d-text)] block">
                    {t(link.labelKey, link.fallback)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}

      {showSpiritual ? (
        <section className="space-y-2">
          <h2 className="font-bold text-[var(--d28d-text)]">Viento Recio</h2>
          <SpiritualHomeButton onOpen={onOpenSpiritual} />
        </section>
      ) : null}
    </div>
  );
}
